<template>
  <div id="divLoginMfaVerificationWrapper" class="mfa-page">
    <div class="mfa-card">
      <VueButton
        class="mfa-back"
        :iconName="ICON_VARIABLES.arrowLeft.name"
        :iconSize="{
          width: ICON_VARIABLES.arrowLeft.width,
          height: ICON_VARIABLES.arrowLeft.height,
        }"
        iconColor="rgb(235, 56, 50)"
        :ghost="true"
        @click="handleBackClick"
      />

      <div v-if="step === 'info'">
        <VueText sizeLevel="7" weightLevel="5" color="black-100" class="mfa-title">
          İki Aşamalı Giriş
        </VueText>
        <VueText class="mfa-description">
          Bayiniz altında iki aşamalı giriş aktif hale getirilmiştir. Sonu {{ maskedPhone }} ile
          biten telefon numaranıza bir doğrulama kodu gönderilecektir.
        </VueText>

        <div v-if="isBanned" class="mfa-message mfa-message--error">
          Çok fazla deneme yaptınız. {{ banLabel }}
        </div>
        <div v-if="sendError" class="mfa-message mfa-message--error">{{ sendError }}</div>

        <BrandButton class="mfa-submit" :disabled="isBanned || isFetching" @click="handleSendClick">
          <VueText>Gönder</VueText>
        </BrandButton>
      </div>

      <div v-else-if="step === 'verify'">
        <VueText sizeLevel="7" weightLevel="5" color="black-100" class="mfa-title">
          İki Aşamalı Giriş Doğrulama
        </VueText>
        <VueText class="mfa-description">
          Sonu {{ maskedPhone }} ile biten telefon numaranıza gelen 6 haneli doğrulama kodunu
          giriniz.
        </VueText>

        <div
          v-if="timerLabel"
          class="mfa-timer"
          :class="{ 'mfa-timer--warning': secondsLeft < 60 }"
        >
          Kodun geçerlilik süresi: <strong>{{ timerLabel }}</strong>
        </div>
        <div v-else-if="isExpired" class="mfa-timer mfa-timer--expired">
          Kodun süresi doldu. Lütfen yeni kod isteyin.
        </div>

        <div class="mfa-otp">
          <OtpInput v-model="verificationCode" :has-error="!!codeError" :autofocus="true" />
        </div>
        <div v-if="codeError" class="mfa-message mfa-message--error">{{ codeError }}</div>

        <BrandButton
          v-if="isExpired || isBanned"
          class="mfa-resend"
          :disabled="isFetching"
          @click="handleResendClick"
        >
          <VueText>Kodu Yeniden Gönder</VueText>
        </BrandButton>

        <BrandButton
          class="mfa-submit"
          :disabled="isInputDisabled || isFetching"
          @click="handleVerifyClick"
        >
          <VueText>Doğrula</VueText>
        </BrandButton>
      </div>
    </div>
  </div>
</template>

<script>
import RoutesRoot from '@/router/routes/RoutesRoot.js';
import RoutesAthentication from '@/router/routes/RoutesAthentication.js';
import RoutesLogin from '@/router/routes/authentication/RoutesLogin.js';
import VueText from '@/components/shared/VueText/VueText.vue';
import BrandButton from '@/components/brand/BrandButton/BrandButton.vue';
import OtpInput from '@/components/mfa/OtpInput.vue';
import { ICON_VARIABLES } from '@/constants/component.constants.js';
import VueButton from '@/components/shared/VueButton/VueButton.vue';
import Mfa from '@/services/Api/mfa';
import StorageHelper from '@/utils/storageHelper';
import AccountMixin from '@/utils/accountUtils.js';
import { UserTokenRoles } from '@/constants/userTokenRoles.js';
import { mapGetters } from 'vuex';
import { extractErrorMessage } from '../../../../../utils/errorExtractor';

export default {
  name: 'LoginMfaVerification',
  components: {
    VueText,
    BrandButton,
    VueButton,
    OtpInput,
  },
  mixins: [AccountMixin],
  data() {
    return {
      step: null,
      verificationCode: '',
      codeError: '',
      sendError: '',
      secondsLeft: null,
      timerInterval: null,
      isBanned: false,
      unlockDate: null,
      remainingAttempts: 3,
      localExpiresAt: null,
      maskedPhoneValue: null,
    };
  },
  computed: {
    ICON_VARIABLES() {
      return ICON_VARIABLES;
    },
    ...mapGetters('auth', { isFetching: 'getLoginFetching' }),
    mfaRole() {
      return this.$route.query.mfaRole || null;
    },
    isRegister() {
      return this.mfaRole === UserTokenRoles.RegisterMFA;
    },
    mfaOrigin() {
      return this.$route.query.mfaOrigin || null;
    },
    timerLabel() {
      if (!this.secondsLeft || this.secondsLeft <= 0) return null;
      const m = Math.floor(this.secondsLeft / 60)
        .toString()
        .padStart(2, '0');
      const s = (this.secondsLeft % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },
    isExpired() {
      return this.localExpiresAt !== null && this.secondsLeft !== null && this.secondsLeft <= 0;
    },
    isInputDisabled() {
      return this.isExpired || this.isBanned;
    },
    banLabel() {
      if (!this.unlockDate) return '';
      const diff = Math.floor((new Date(this.unlockDate) - Date.now()) / 1000 / 60);
      return `${diff} dakika sonra tekrar deneyebilirsiniz.`;
    },
    maskedPhone() {
      const raw =
        this.$route.query.maskedPhone ||
        this.maskedPhoneValue ||
        (this.$store.state.auth.user && this.$store.state.auth.user.phoneNumber);
      if (!raw) return '****';
      const last4 = String(raw)
        .replace(/\D/g, '')
        .slice(-4);
      return last4 ? `***${last4}` : '****';
    },
  },
  created() {
    const storedExpiry = this.getStoredExpiry();
    if (storedExpiry) {
      this.step = 'verify';
      this.localExpiresAt = storedExpiry;
      this.startTimer();
      return;
    }

    if (this.isRegister) {
      this.step = 'info';
    } else {
      this.step = 'verify';
      this.sendCode();
    }
  },
  beforeDestroy() {
    clearInterval(this.timerInterval);
  },
  methods: {
    handleBackClick() {
      this.clearExpiry();
      clearInterval(this.timerInterval);

      const preMfaTokenHelper = new StorageHelper({
        id: process.env.VUE_APP_STORAGE_PRE_MFA_TOKEN,
      });
      const preMfaToken = preMfaTokenHelper.get();
      if (preMfaToken) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_TOKEN_KEY }).set(preMfaToken);
      }
      preMfaTokenHelper.remove();

      if (this.mfaOrigin) {
        this.$router.replace({ name: this.mfaOrigin });
        return;
      }

      this.$router.go(-1);
    },
    getStoredExpiry() {
      try {
        const stored = new StorageHelper({
          id: process.env.VUE_APP_STORAGE_MFA_SMS_COUNTER,
        }).get();
        if (!stored || !stored.expireDate) return null;
        const t = new Date(stored.expireDate).getTime();
        if (Number.isNaN(t) || t <= Date.now()) return null;
        return stored.expireDate;
      } catch (e) {
        return null;
      }
    },
    persistExpiry(expireDate) {
      if (!expireDate) return;
      new StorageHelper({ id: process.env.VUE_APP_STORAGE_MFA_SMS_COUNTER }).set({ expireDate });
    },
    clearExpiry() {
      new StorageHelper({ id: process.env.VUE_APP_STORAGE_MFA_SMS_COUNTER }).remove();
    },
    startTimer() {
      if (!this.localExpiresAt) return;
      clearInterval(this.timerInterval);
      const expireTime = new Date(this.localExpiresAt).getTime();
      const calc = () => {
        this.secondsLeft = Math.max(0, Math.floor((expireTime - Date.now()) / 1000));
      };
      calc();
      this.timerInterval = setInterval(() => {
        calc();
        if (this.secondsLeft === 0) clearInterval(this.timerInterval);
      }, 1000);
    },
    async sendCode() {
      try {
        this.$store.dispatch('auth/setLoginFetching', true);
        const res = await Mfa.postSendCode();
        const Data = res && res.data ? res.data.Data : null;
        if (!Data) return;

        if (Data.isBanned) {
          this.isBanned = true;
          this.unlockDate = Data.unlockDate;
          return;
        }

        this.maskedPhoneValue = Data.maskedPhone || Data.phoneNumber || this.maskedPhoneValue;

        this.isBanned = false;
        this.sendError = '';
        this.codeError = '';
        this.verificationCode = '';
        this.localExpiresAt = Data.expireDate;
        this.persistExpiry(Data.expireDate);
        this.startTimer();
        this.step = 'verify';
      } catch (error) {
        console.error('Send code failed:', error);
        this.sendError = 'Kod gönderilemedi. Lütfen tekrar deneyin.';
      } finally {
        this.$store.dispatch('auth/setLoginFetching', false);
      }
    },
    handleSendClick() {
      this.sendCode();
    },
    handleResendClick() {
      this.resendCode();
    },
    async resendCode() {
      try {
        this.$store.dispatch('auth/setLoginFetching', true);
        const res = await Mfa.postResendCode();
        const Data = res && res.data ? res.data.Data : null;
        if (!Data) return;

        if (Data.isBanned) {
          this.isBanned = true;
          this.unlockDate = Data.unlockDate;
          this.codeError =
            Data.message || 'Çok fazla kod istediniz. Lütfen daha sonra tekrar deneyin.';
          return;
        }

        this.isBanned = false;
        this.remainingAttempts = Data.remainingAttempts != null ? Data.remainingAttempts : 3;
        this.verificationCode = '';
        this.codeError = '';
        this.localExpiresAt = Data.expireDate;
        this.persistExpiry(Data.expireDate);
        this.startTimer();
      } catch (error) {
        console.error('Resend failed:', error);
        this.codeError = 'Kod gönderilemedi. Lütfen tekrar deneyin.';
      } finally {
        this.$store.dispatch('auth/setLoginFetching', false);
      }
    },
    async handleVerifyClick() {
      if (this.verificationCode.length !== 6) {
        this.codeError = 'Lütfen 6 haneli kodu giriniz.';
        return;
      }
      this.codeError = '';

      try {
        this.$store.dispatch('auth/setLoginFetching', true);
        const res = await Mfa.postVerifyCode(this.verificationCode);
        const Data = res && res.data ? res.data.Data : null;
        if (!Data) return;

        if (Data.isBanned || Data.isBlocked) {
          this.isBanned = true;
          this.unlockDate = Data.unlockDate;
          this.codeError = Data.message || 'Çok fazla deneme yaptınız.';
          return;
        }

        if (!Data.success) {
          this.remainingAttempts = Data.remainingAttempts;
          this.verificationCode = '';
          this.codeError =
            Data.remainingAttempts > 0
              ? `${Data.message} ${Data.remainingAttempts} deneme hakkınız kaldı.`
              : Data.message;
          return;
        }

        this.clearExpiry();
        await this.completeLogin(Data);
      } catch (error) {
        this.$store.dispatch('auth/setLoginError', error);
        this.codeError =
          extractErrorMessage(error) || 'Doğrulama başarısız. Lütfen tekrar deneyin.';
      } finally {
        this.$store.dispatch('auth/setLoginFetching', false);
      }
    },
    async completeLogin(Data) {
      new StorageHelper({ id: process.env.VUE_APP_STORAGE_PRE_MFA_TOKEN }).remove();

      const { token, user, refreshToken, pos, staffValidation } = Data;

      await this.$store.dispatch('auth/setAuthData', Data);

      if (token) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_TOKEN_KEY }).set(token);
      }
      if (user) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_CURRENT_USER_KEY }).set(user);
      }
      if (refreshToken && refreshToken.token) {
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_REFRESH_TOKEN_KEY }).set(
          refreshToken.token,
        );
      }

      if (pos && Array.isArray(pos) && pos.length > 1) {
        await this.$store.dispatch('auth/setPosList', pos);
        this.$router.push(
          `${RoutesRoot.Authentication.path}/${RoutesAthentication.Login.path}/${RoutesLogin.SelectPos.path}`,
        );
      } else if (pos) {
        const posValue = Array.isArray(pos) ? pos[0] : pos;
        new StorageHelper({ id: process.env.VUE_APP_STORAGE_CURRENT_POS_KEY }).set(posValue);
        this.accountLogin(posValue.posChannel, staffValidation, user);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.mfa-page {
  display: flex;
  justify-content: center;
  padding: 48px 16px;
}

.mfa-card {
  width: 100%;
  max-width: 400px;
  padding: 28px 20px 24px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.mfa-title {
  display: block;
  margin-bottom: 16px;
}

.mfa-back {
  display: block;
  width: auto;
  margin: 0 0 20px;
}

.mfa-description {
  display: block;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 16px;
  line-height: 1.5;
  margin-bottom: 20px;
  text-align: center;
}

.mfa-timer {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 16px;

  &--warning {
    color: #e65100;
  }

  &--expired {
    color: #d32f2f;
  }
}

.mfa-otp {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.mfa-message {
  font-size: 13px;
  margin-bottom: 16px;

  &--error {
    color: #d32f2f;
  }
}

.mfa-resend {
  width: 100%;
  margin-bottom: 12px;
}

.mfa-submit {
  width: 100%;
}
</style>
